// ./src/theme/index.js
import { createTheme } from "@mantine/core"

export const theme = createTheme({
  colorScheme: "light",
  primaryColor: "brand",
  colors: {
    // CarExpenses brand - Deep Forest Green
    // Conveys: Trust, financial stability, eco-conscious driving, reliability
    brand: [
      "#f0fdf4", // 0 - Lightest backgrounds, subtle highlights
      "#dcfce7", // 1 - Very light hover states
      "#bbf7d0", // 2 - Light secondary backgrounds
      "#86efac", // 3 - Medium light borders, dividers
      "#4ade80", // 4 - Medium secondary elements
      "#22c55e", // 5 - Bright green for positive states
      "#16a34a", // 6 - Primary brand color - main CTAs
      "#15803d", // 7 - Darker hover on primary
      "#166534", // 8 - Dark active states, headings
      "#14532d", // 9 - Darkest, near-black green
    ],
    // Accent - Amber/Gold
    // Dashboard indicators, attention states, premium feel
    accent: [
      "#fffbeb", // 0
      "#fef3c7", // 1
      "#fde68a", // 2
      "#fcd34d", // 3
      "#fbbf24", // 4 - Main accent
      "#f59e0b", // 5
      "#d97706", // 6
      "#b45309", // 7
      "#92400e", // 8
      "#78350f", // 9
    ],
    // Fuel - Petrol Blue/Cyan
    // Fuel tracking, energy, motion
    fuel: [
      "#ecfeff", // 0
      "#cffafe", // 1
      "#a5f3fc", // 2
      "#67e8f9", // 3
      "#22d3ee", // 4
      "#06b6d4", // 5 - Main fuel cyan
      "#0891b2", // 6
      "#0e7490", // 7
      "#155e75", // 8
      "#164e63", // 9
    ],
    // Success - Emerald
    // Paid expenses, completed maintenance, positive states
    success: [
      "#ecfdf5",
      "#d1fae5",
      "#a7f3d0",
      "#6ee7b7",
      "#34d399",
      "#10b981", // Main success
      "#059669",
      "#047857",
      "#065f46",
      "#064e3b",
    ],
    // Error - Engine Red
    // Overdue maintenance, warnings, negative states
    error: [
      "#fef2f2",
      "#fee2e2",
      "#fecaca",
      "#fca5a5",
      "#f87171",
      "#ef4444", // Main error
      "#dc2626",
      "#b91c1c",
      "#991b1b",
      "#7f1d1d",
    ],
    // Warning - Orange (Check Engine Light)
    // Upcoming maintenance, moderate alerts
    warning: [
      "#fff7ed",
      "#ffedd5",
      "#fed7aa",
      "#fdba74",
      "#fb923c",
      "#f97316", // Main warning
      "#ea580c",
      "#c2410c",
      "#9a3412",
      "#7c2d12",
    ],
    // Neutral - Cool Gray
    // Professional, clean UI backgrounds
    neutral: [
      "#ffffff", // White
      "#fafafa", // Off-white backgrounds
      "#f4f4f5", // Light gray backgrounds
      "#e4e4e7", // Borders, dividers
      "#d4d4d8", // Disabled states
      "#a1a1aa", // Placeholder text
      "#71717a", // Secondary text
      "#52525b", // Body text
      "#27272a", // Headings
      "#18181b", // Near black
    ],
  },
  // Bricolage Grotesque for headings - bold, characterful, modern automotive
  // Manrope for body - clean, geometric, excellent for data-heavy interfaces
  fontFamily:
    "'Manrope', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontFamilyMonospace:
    "'JetBrains Mono', 'Fira Code', 'Source Code Pro', monospace",
  headings: {
    fontFamily:
      "'Bricolage Grotesque', 'Space Grotesk', -apple-system, sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "3.5rem", lineHeight: "1.1", fontWeight: "800" },
      h2: { fontSize: "2.25rem", lineHeight: "1.2", fontWeight: "700" },
      h3: { fontSize: "1.5rem", lineHeight: "1.3", fontWeight: "700" },
      h4: { fontSize: "1.25rem", lineHeight: "1.4", fontWeight: "600" },
      h5: { fontSize: "1rem", lineHeight: "1.5", fontWeight: "600" },
      h6: { fontSize: "0.875rem", lineHeight: "1.5", fontWeight: "600" },
    },
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
  },
  radius: {
    xs: "0.25rem",
    sm: "0.375rem",
    md: "0.625rem",
    lg: "1rem",
    xl: "1.5rem",
    "2xl": "2rem",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.04)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.08)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  },
  other: {
    // Primary brand colors
    brandPrimary: "#16a34a",
    brandDark: "#14532d",
    brandLight: "#f0fdf4",

    // Accent for highlights
    accentAmber: "#fbbf24",

    // Fuel color
    fuelCyan: "#06b6d4",

    // Semantic colors
    successGreen: "#10b981",
    errorRed: "#ef4444",
    warningOrange: "#f97316",

    // Background variations
    bgPrimary: "#ffffff",
    bgSecondary: "#fafafa",
    bgTertiary: "#f4f4f5",
    bgBrand: "#f0fdf4",

    // Text colors
    textPrimary: "#27272a",
    textSecondary: "#71717a",
    textDimmed: "#a1a1aa",

    // Border colors
    borderLight: "#e4e4e7",
    borderMedium: "#d4d4d8",

    // Gradients for hero sections and CTAs
    gradientPrimary: "linear-gradient(135deg, #166534 0%, #22c55e 100%)",
    gradientHero:
      "linear-gradient(135deg, #14532d 0%, #166534 50%, #22c55e 100%)",
    gradientSubtle: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
    gradientDark: "linear-gradient(135deg, #0f172a 0%, #14532d 100%)",

    // Brand shadows
    shadowBrand: "0 4px 14px 0 rgb(22 101 52 / 0.2)",
    shadowBrandLg: "0 10px 40px 0 rgb(22 101 52 / 0.25)",
    shadowAccent: "0 4px 14px 0 rgb(245 158 11 / 0.25)",

    // Glow effects
    glowBrand: "0 0 20px rgb(22 163 74 / 0.3)",
    glowAccent: "0 0 20px rgb(251 191 36 / 0.4)",
  },
})
